import { Card } from "@podcastsoundboard/ps-lib/ui"
import * as React from "react"
import { useNavigate } from "react-router"

import deletePadAction from "../../../../api/pads/delete"
import audioStack from "../../../../lib/audioStack"
import { useAppDispatch, useAppSelector } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import Pad from "../../../../types/Pad"
import { Soundboard } from "../../../../types/Soundboard"
import PadComponent from "../Pad"
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import AddCircleIcon from "@podcastsoundboard/ps-lib/ui/icons/AddCircle"
import AudioFileIcon from "@podcastsoundboard/ps-lib/ui/icons/AudioFile"
import DeleteIcon from "@podcastsoundboard/ps-lib/ui/icons/Delete"
import LockIcon from "@podcastsoundboard/ps-lib/ui/icons/Lock"

const DEFAULT_COLOR = "#AAA"

const MAX_FREE_PADS_INDEX = 2

const LockIconStyled = (props: React.ComponentProps<typeof LockIcon>) => (
  <LockIcon {...props} className="text-text-primary" />
)
const AddCircleIconStyled = (
  props: React.ComponentProps<typeof AddCircleIcon>,
) => <AddCircleIcon {...props} className="text-text-primary" />
const AudioFileIconStyled = (
  props: React.ComponentProps<typeof AudioFileIcon>,
) => <AudioFileIcon {...props} className="text-text-primary" />
const DeleteIconStyled = (props: React.ComponentProps<typeof DeleteIcon>) => (
  <DeleteIcon {...props} className="text-text-primary" />
)

const soundboardPadBreakpoints = {
  xs: 6,
  sm: 3,
  md: 3,
  lg: 2,
  xl: 1.5,
}

export default function SoundboardComponent({
  trialMode,
  setNewPadModalOpen,
  setNewSoundboardModalOpen,
  onPadUpdate,
  overrideSoundboard,
  soundboard,
  overrideOnCogPress,
  publicMode,
}: {
  trialMode: boolean
  setNewPadModalOpen?: (open: boolean) => void
  setNewSoundboardModalOpen?: (open: boolean) => void
  onPadUpdate?: () => void
  overrideSoundboard?: Soundboard
  soundboard: Soundboard
  overrideOnCogPress?: () => void
  publicMode?: boolean
}) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [deleting, setDeleting] = React.useState(false)
  const [dragDropSaving, setDragDropSaving] = React.useState(false)
  const searchBarValue = useAppSelector((state) => state.searchBarValue)

  const pads = ((soundboard && soundboard.pads) || []).filter((k) =>
    searchBarValue
      ? k.name?.toLowerCase().includes(searchBarValue.toLowerCase())
      : true,
  )

  const deletePad = (pad: Pad) => {
    setDeleting(true)
    deletePadAction(pad.uuid)
      .then(() => {
        setDeleting(false)
        onPadUpdate && onPadUpdate()
      })
      .catch((err) => {
        setDeleting(false)
        console.error("Error deleting pad", err)
        const text =
          (err.response?.data as { error?: string } | undefined)?.error ||
          "Failed to delete pad."
        dispatch(addSnackbar({ text }))
      })
  }

  if (!soundboard && setNewSoundboardModalOpen)
    return (
      <Grid item {...soundboardPadBreakpoints}>
        <Card
          elevation={2}
          onClick={() => setNewSoundboardModalOpen(true)}
          style={{
            cursor: "pointer",
            opacity: 0.5,
            width: "100%",
            height: "100%",
            color: DEFAULT_COLOR,
            border: `1px solid ${DEFAULT_COLOR}`,
            borderRadius: "4px",
          }}
        >
          <Box
            className="p-8"
            style={{ height: "100%" }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <IconButton aria-label="Create Soundboard">
              <AddCircleIconStyled />
            </IconButton>
            <Typography variant="body1" color="textPrimary" textAlign="center">
              Create Soundboard
            </Typography>
          </Box>
        </Card>
      </Grid>
    )

  return (
    <>
      {dragDropSaving && (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            backgroundColor: "rgba(0,0,0,0.6)",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {pads.map((pad, i) => (
        <Grid key={pad.uuid} item {...soundboardPadBreakpoints}>
          <Box style={{ position: "relative", height: "100%" }}>
            <Box
              className={i > MAX_FREE_PADS_INDEX && trialMode ? "blur-pad" : ""}
              style={{ height: "100%" }}
            >
              <PadComponent
                overrideOnCogPress={overrideOnCogPress}
                solo={soundboard?.solo || false}
                dimPlayedTracks={soundboard?.dimPlayedTracks || false}
                audioInterface={audioStack.interfaceForSoundboardPad(
                  soundboard.uuid,
                  pad.uuid,
                )}
                onPadUpdate={onPadUpdate}
                keyIndex={i}
                overrideSoundboard={overrideSoundboard}
                pad={pad}
                disableDragDrop={publicMode}
                setDragDropSaving={setDragDropSaving}
              />
            </Box>
            {i > MAX_FREE_PADS_INDEX && trialMode && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  cursor: "pointer",
                }}
                className="p-8 px-2"
              >
                <Box
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                  onClick={() => navigate("/app/account/subscription")}
                />
                <LockIconStyled className="my-2 text-text-primary" />
                <Typography
                  className="leading-[1]"
                  variant="body1"
                  color="textPrimary"
                  textAlign="center"
                >
                  Max 3 sounds without subscription.
                </Typography>
                {!deleting && (
                  <Tooltip title="Delete pad">
                    <IconButton
                      aria-label="Delete Pad"
                      className="relative mt-0"
                      onClick={() => deletePad(pad)}
                    >
                      <DeleteIconStyled />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      ))}

      {!publicMode && (
        <Grid item {...soundboardPadBreakpoints}>
          <Card
            elevation={2}
            onClick={() => setNewPadModalOpen && setNewPadModalOpen(true)}
            style={{
              cursor: "pointer",
              opacity: 0.5,
              width: "100%",
              height: "100%",
              color: DEFAULT_COLOR,
              border: `1px solid ${DEFAULT_COLOR}`,
              borderRadius: "4px",
            }}
          >
            <Box
              className="p-8"
              style={{ height: "100%" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <IconButton aria-label="Add Sound">
                <AudioFileIconStyled />
              </IconButton>
              <Typography
                variant="body1"
                color="textPrimary"
                textAlign="center"
              >
                Add Sound
              </Typography>
            </Box>
          </Card>
        </Grid>
      )}
    </>
  )
}
